import { useState } from 'react'
import { Trash2, Plus, RefreshCw, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useUser } from '@/hooks/use-user'
import { usePaymentConfig } from '@/hooks/use-payment-config'
import { formatShortCryptoAddress } from '@/utils/format'
import type { SavedCard } from '@/types/payment-methods'
import CardBrandIcon from '@/components/CardBrandIcon'
import ConfirmModal from '@/components/common/ConfirmationModal'
import logoUrl from '@/assets/logo.png'

interface PaymentMethodsListProps {
	cards: SavedCard[]
	isLoading: boolean
	isCardExpired: (card: SavedCard) => boolean
	onAddCard: () => void
	onUpdateCard: (cardId: number) => void
	onDeleteCard: (cardId: number) => Promise<void>
}

export default function PaymentMethodsList({
	cards,
	isLoading,
	isCardExpired,
	onAddCard,
	onUpdateCard,
	onDeleteCard,
}: PaymentMethodsListProps) {
	const { walletAddress } = useUser()
	const { cardEnabled } = usePaymentConfig()
	const [deletingCardId, setDeletingCardId] = useState<number | null>(null)
	const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)
	const [isDeletingInProgress, setIsDeletingInProgress] = useState(false)

	const handleDelete = async () => {
		if (!confirmDeleteId) return
		setIsDeletingInProgress(true)
		try {
			await onDeleteCard(confirmDeleteId)
		} finally {
			setIsDeletingInProgress(false)
			setConfirmDeleteId(null)
			setDeletingCardId(null)
		}
	}

	const formatExpiry = (month: number, year: number) => {
		return `${String(month).padStart(2, '0')}/${String(year).slice(-2)}`
	}

	return (
		<div className="w-full mt-6">
			<h3 className="font-figtree font-semibold text-[14px] leading-[16px] text-[#BABABB] mb-3 px-1">
				Payment Methods
			</h3>

			<div className="space-y-2">
				{/* SECH Wallet — always first, not deletable */}
				<div className="w-full bg-[#2F2F32] rounded-lg px-4 py-3 flex items-center justify-between">
					<div className="flex items-center gap-3">
						<img src={logoUrl} alt="SECH" className="h-8 w-8 rounded-full" />
						<div>
							<div className="font-figtree text-sm font-medium text-white">
								SECH Wallet
							</div>
							{walletAddress && (
								<div className="font-figtree text-xs text-[#BABABB]">
									{formatShortCryptoAddress(walletAddress)}
								</div>
							)}
						</div>
					</div>
				</div>

				{/* Loading state */}
				{isLoading && (
					<div className="flex items-center justify-center py-4">
						<Loader2 className="h-5 w-5 animate-spin text-[#BABABB]" />
					</div>
				)}

				{/* Saved cards */}
				{cards.map((card) => {
					const expired = isCardExpired(card)
					const needsUpdate = !!card.updateRequired
					const isDeleting = isDeletingInProgress && deletingCardId === card.id
					return (
						<div
							key={card.id}
							className={`w-full bg-[#2F2F32] rounded-lg px-4 py-3 transition-opacity ${
								expired || needsUpdate || isDeleting ? 'opacity-60' : ''
							}`}
						>
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-3">
									<CardBrandIcon brand={card.cardBrand} width={40} className="shrink-0" />
									<div>
										<div className="font-figtree text-sm font-medium text-white">
											•••• {card.lastFour}
										</div>
										<div className="font-figtree text-xs text-[#BABABB]">
											Exp {formatExpiry(card.expiryMonth, card.expiryYear)}
											{card.cardholderName && ` · ${card.cardholderName}`}
										</div>
									</div>
								</div>

								<div className="flex items-center gap-2">
									{expired ? (
										<span className="text-xs font-figtree font-medium text-red-400 bg-red-400/10 px-2 py-0.5 rounded">
											Expired
										</span>
									) : needsUpdate && (
										<span className="text-xs font-figtree font-medium text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded">
											Update Required
										</span>
									)}

									{isDeleting ? (
										<Loader2 className="h-4 w-4 animate-spin text-[#BABABB]" />
									) : (
										<>
											{(expired || needsUpdate) && (
												<button
													onClick={() => onUpdateCard(card.id)}
													className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
													title="Update card"
												>
													<RefreshCw className="h-4 w-4 text-[#BABABB]" />
												</button>
											)}

											<button
												onClick={() => {
													setDeletingCardId(card.id)
													setConfirmDeleteId(card.id)
												}}
												className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
												title="Remove card"
											>
												<Trash2 className="h-4 w-4 text-[#BABABB]" />
											</button>
										</>
									)}
								</div>
							</div>

							{/* Re-entry prompt for cards whose gateway vault was recreated */}
							{needsUpdate && !expired && (
								<p className="mt-2 font-figtree text-xs text-amber-400/90">
									This card needs to be re-entered before it can be used again.
								</p>
							)}
						</div>
					)
				})}

				{/* Add Payment Method button — hidden when card payments are disabled */}
				{cardEnabled ? (
					<Button
						variant="ghost"
						onClick={onAddCard}
						className="w-full h-[42px] bg-[#2F2F32] rounded-lg px-4 flex items-center justify-center gap-2 text-primary hover:bg-[#3C3C3C] hover:text-primary"
					>
						<Plus className="h-4 w-4" />
						<span className="font-figtree text-sm font-medium">Add Payment Method</span>
					</Button>
				) : (
					<p className="text-xs font-figtree text-[#BABABB] text-center px-2 pt-2">
						Card payments are currently disabled. You can remove existing cards, but new cards cannot be added.
					</p>
				)}
			</div>

			{/* Delete confirmation */}
			<ConfirmModal
				open={!!confirmDeleteId}
				onClose={() => {
					if (isDeletingInProgress) return
					setConfirmDeleteId(null)
					setDeletingCardId(null)
				}}
				onConfirm={handleDelete}
				title="Remove Card"
				description="Are you sure you want to remove this payment method? This cannot be undone."
				confirmText={isDeletingInProgress ? 'Removing...' : 'Remove'}
				cancelText="Cancel"
				variant="delete"
				isLoading={isDeletingInProgress}
			/>
		</div>
	)
}
